import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, Col } from 'reactstrap';
import { AppStateContext, AppDispatchContext } from '../../context/AppContext';
import {
    StyledInput,
    AccentButton,
    StyledFormGroup,
    StyledDefaultModalHeader,
    StyledDefaultModalBody,
    StyledDefaultModalFooter,
    StyledAlert,
    SpinnerPaddingRight,
} from '../../styles/common';

import { useDidMount, useHandleEnter, usePostData } from '../../utils/hooks';

import {
    ReportsStateContext,
    ReportsDispatchContext,
} from '../../context/ReportContext';

import { reportsGraphQLBody } from '../../utils/graphQL';

import {
    buildVisibleColumns,
    generateCsv,
    generateXlsx,
    generatePdf,
} from '../../utils/exportGenerators';

const SUPPORTED_FORMATS = new Set(['csv', 'xlsx', 'pdf']);

const ExportReport = () => {
    const [input, setInput] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [building, setBuilding] = useState(false);
    const [downloaded, setDownloaded] = useState(false);
    const dispatch = useContext(AppDispatchContext);
    const {
        searchData,
        currentReport,
        currentSearchRules,
        sortBy,
        sortDir,
        searchResultsMetadata: {
            count: searchResultsCount,
            pi: { offset: piOffset, hasNext: piHasNext },
            ucms: { offset: ucmsOffset, hasNext: ucmsHasNext },
        },
    } = useContext(ReportsStateContext);
    const reportsDispatch = useContext(ReportsDispatchContext);
    const { activeDevicesColumns, modal, user } = useContext(AppStateContext);
    const { t } = useTranslation();

    const format = SUPPORTED_FORMATS.has(modal?.format) ? modal.format : 'csv';

    const inputRef = useRef();
    inputRef.current = input;

    // Snapshot inputs at submit-time so a mid-export filter/column change
    // does not produce a mixed file (PRD edge case).
    const snapshotRef = useRef(null);

    useDidMount(() => {
        const reportName = currentReport.name || 'New Report';
        setInput(reportName);
    });

    const [searchCallback, { data: searchDataResponse }] = usePostData({
        graphQL: true,
    });

    const formatLabelKey = useMemo(() => {
        if (format === 'xlsx') return 'modalLabels$exportReport$xlsx';
        if (format === 'pdf') return 'modalLabels$exportReport$pdf';
        return 'modalLabels$exportReport$csv';
    }, [format]);

    const runExport = async () => {
        const snap = snapshotRef.current;
        if (!snap) return;
        const { rows, visibleColumns, filename, reportMeta } = snap;
        if (format === 'xlsx') {
            await generateXlsx({ rows, visibleColumns, filename, reportMeta });
        } else if (format === 'pdf') {
            generatePdf({ rows, visibleColumns, filename, reportMeta, t });
        } else {
            generateCsv({ rows, visibleColumns, filename });
        }
    };

    const submit = () => {
        if (!inputRef.current.length) return;
        const orderedFieldKeys = (currentReport && currentReport.updatedFields) || [];
        const visibleColumns = buildVisibleColumns({
            orderedFieldKeys,
            activeDevicesColumns,
            t,
        });
        snapshotRef.current = {
            rows: [...(searchData || [])],
            visibleColumns,
            filename: inputRef.current,
            reportMeta: {
                reportName: inputRef.current,
                currentSearchRules: currentSearchRules ? [...currentSearchRules] : [],
                exportedBy: (user && (user.email || user.username)) || '',
                totalRows: searchResultsCount,
            },
        };
        setDownloading(true);
    };

    useEffect(() => {
        if (searchDataResponse) {
            const searchDataInfo =
                searchDataResponse &&
                searchDataResponse.data &&
                searchDataResponse.data.deviceInfo &&
                searchDataResponse.data.deviceInfo;
            if (searchDataInfo) {
                searchDataInfo.pageInfo &&
                    reportsDispatch({
                        type: 'SET_SEARCH_RESULTS_METADATA',
                        payload: searchDataInfo.pageInfo,
                    });
                searchDataInfo.devices &&
                    reportsDispatch({
                        type: 'APPEND_TO_SEARCH_DATA',
                        payload: searchDataInfo.devices,
                    });
            }
        }
    }, [searchDataResponse, reportsDispatch]);

    useEffect(() => {
        if (!downloading) return;
        if (piHasNext || ucmsHasNext) {
            searchCallback(
                reportsGraphQLBody({
                    searchFilters: currentSearchRules,
                    sortBy,
                    sortDir,
                    limit: 50,
                    piOffset,
                    ucmsOffset,
                    searchUcms: ucmsHasNext,
                    searchPi: piHasNext,
                    fields: currentReport.updatedFields,
                }),
            );
            return;
        }
        if (snapshotRef.current) {
            snapshotRef.current = {
                ...snapshotRef.current,
                rows: [...(searchData || [])],
                reportMeta: {
                    ...snapshotRef.current.reportMeta,
                    totalRows: (searchData || []).length || searchResultsCount,
                },
            };
        }
        setBuilding(true);
        Promise.resolve()
            .then(runExport)
            .finally(() => {
                setBuilding(false);
                setDownloaded(true);
            });
    }, [downloading, searchData, piHasNext, ucmsHasNext]);

    useHandleEnter({ action: submit });

    const statusLabel = (() => {
        if (downloaded) return t('modalLabels$exportReport$downloaded');
        if (building) return t('modalLabels$exportReport$building') || 'Building file';
        return t('modalLabels$exportReport$downloading');
    })();

    const inProgressLabel = building
        ? t('modalLabels$exportReport$building') || 'Building file'
        : t('modalLabels$exportReport$downloading');

    const totalForDisplay = searchResultsCount || (searchData && searchData.length) || 0;

    return (
        <>
            <StyledDefaultModalHeader>
                {t('modalLabels$exportReport$title')}
            </StyledDefaultModalHeader>
            <StyledDefaultModalBody>
                <StyledAlert color="info">
                    {t('modalLabels$exportReport$info')}
                </StyledAlert>
                <StyledFormGroup row autoComplete="off">
                    <Col xs="3">
                        <Label for="reportTitle">
                            {t('modalLabels$exportReport$input')} :
                        </Label>
                    </Col>
                    <Col xs="6">
                        <StyledInput
                            name="reportTitle"
                            bsSize="sm"
                            value={input}
                            valid={input.length}
                            invalid={!input.length}
                            onChange={(e) => setInput(e.target.value)}
                        />
                    </Col>
                </StyledFormGroup>
                <StyledFormGroup row autoComplete="off">
                    <Col xs="3">{t('modalLabels$exportReport$filetype')} :</Col>
                    <Col xs="6">{t(formatLabelKey)}</Col>
                </StyledFormGroup>
                {downloading && (
                    <StyledFormGroup row autoComplete="off">
                        <Col xs="3" />
                        <Col xs="6">
                            {`${statusLabel} ${
                                searchData.length
                            }/${totalForDisplay} ${t(
                                'modalLabels$exportReport$entries',
                            )}${downloaded ? '' : ' ...'}`}
                        </Col>
                    </StyledFormGroup>
                )}
            </StyledDefaultModalBody>
            <StyledDefaultModalFooter>
                {!downloaded && (
                    <AccentButton
                        outline
                        color="primary"
                        size="sm"
                        onClick={submit}
                        disabled={!input.length || downloading}
                    >
                        {downloading ? (
                            <>
                                <SpinnerPaddingRight size="sm" />
                                {inProgressLabel}
                            </>
                        ) : (
                            t('modalLabels$exportReport$submit')
                        )}
                    </AccentButton>
                )}
                <AccentButton
                    outline
                    color="primary"
                    size="sm"
                    onClick={() => {
                        setDownloading(false);
                        dispatch({
                            type: 'CLEAR_MODAL',
                        });
                    }}
                >
                    {downloaded
                        ? t('modalElementLabels$details$modal$done')
                        : t('modalElementLabels$details$modal$cancel')}
                </AccentButton>
            </StyledDefaultModalFooter>
        </>
    );
};

export default ExportReport;
